import { useEffect, useState } from 'react';
import { expensesApi as eapi } from '../../../api/expenses.js';

/**
 * Policy & mileage-rate admin: list expense policies and mileage rates, and
 * create a new policy. Money fields are integer cents.
 */
export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [rates, setRates] = useState([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([eapi.listPolicies(), eapi.listMileageRates()]);
      setPolicies(p?.items || []);
      setRates(m?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function createPolicy(e) {
    e.preventDefault();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await eapi.createPolicy({ name, active: true });
      setName('');
      await load();
    } catch (e2) {
      setError(e2.response?.data?.message || 'Failed to create policy');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="expenses-policies-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Policies</h1>
          <p className="muted">Expense policies and mileage rates that govern report checks.</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="expenses-policies-error">{error}</div>}

      <form onSubmit={createPolicy} className="card form-card row-form">
        <label>Policy name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Travel Policy" />
        </label>
        <div className="actions">
          <button className="btn primary" type="submit" data-testid="expenses-policy-create" disabled={busy || !name}>Create policy</button>
        </div>
      </form>

      <div className="card">
        <h3>Policies</h3>
        <table className="data-table" data-testid="expenses-policy-list">
          <thead><tr><th>Name</th><th>Description</th><th>Active</th></tr></thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>{p.description || '—'}</td>
                <td>{p.active ? 'yes' : 'no'}</td>
              </tr>
            ))}
            {!loading && policies.length === 0 && <tr><td colSpan={3} className="muted">No policies.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Mileage rates</h3>
        <table className="data-table" data-testid="expenses-mileage-list">
          <thead><tr><th>Jurisdiction</th><th>Rate</th><th>Unit</th><th>Currency</th><th>Active</th></tr></thead>
          <tbody>
            {rates.map((m) => (
              <tr key={m.id}>
                <td>{m.jurisdiction}</td>
                <td>{(Number(m.rate_cents_per_unit || 0) / 100).toFixed(2)}</td>
                <td>{m.unit}</td>
                <td>{m.currency}</td>
                <td>{m.active ? 'yes' : 'no'}</td>
              </tr>
            ))}
            {!loading && rates.length === 0 && <tr><td colSpan={5} className="muted">No mileage rates.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
